import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaDownload, FaPrint, FaSave } from "react-icons/fa";
import { toast } from "react-toastify";
import Navbar from "./Navbar";
import axiosInstance from "../config/axios";
import { buildCsv, downloadCsvFile } from "../utils/csvDownload";
import logo from "../Assets/logo3.jpg";
import {
  INVOICE_DETAIL_COLUMNS,
  INVOICE_DETAIL_STATUS_FILTER,
  buildInvoiceDetailAgentOptions,
  buildInvoiceDetailDateOptions,
  buildInvoiceDetailRow,
  buildInvoiceDetailRows,
  buildInvoiceDetailTradeOptions,
  formatInvoiceDetailPeriodLabel,
  getInvoiceDetailSelectOptions,
  invoiceDetailRowToTradePatch,
} from "../utils/invoiceDetailReportUtils";

const DEFAULT_COMPANY = "ABC Realty Inc., Brokerage";
const REPORT_TITLE = "Invoice Detail Report";

const cellSelectClass =
  "w-full min-w-[6rem] px-1.5 py-1 text-xs border border-gray-200 rounded bg-white focus:ring-1 focus:ring-blue-600 focus:outline-none";
const cellInputClass =
  "w-full min-w-[4.5rem] px-1.5 py-1 text-xs border border-gray-200 rounded bg-white focus:ring-1 focus:ring-blue-600 focus:outline-none";

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const InvoiceDetailReport = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isFinancePage = pathname.startsWith("/finance-");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [companyName, setCompanyName] = useState(DEFAULT_COMPANY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showReport, setShowReport] = useState(false);
  /** All trades in period (editable). Status filter only affects what is shown. */
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [allTrades, setAllTrades] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [agentRes, profileRes] = await Promise.all([
          axiosInstance.get("/agents"),
          axiosInstance.get("/company-profile").catch(() => ({ data: null })),
        ]);
        setAgents(agentRes.data || []);
        const name = profileRes?.data?.companyName;
        if (name) setCompanyName(name);
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, []);

  const visibleRows = useMemo(() => {
    if (!statusFilter) return rows;
    return rows.filter((row) => row.status === statusFilter);
  }, [rows, statusFilter]);

  const tradeOptions = useMemo(
    () => buildInvoiceDetailTradeOptions(visibleRows),
    [visibleRows],
  );
  const agentOptions = useMemo(
    () => buildInvoiceDetailAgentOptions(agents, visibleRows),
    [agents, visibleRows],
  );
  const dateOptions = useMemo(
    () => buildInvoiceDetailDateOptions(visibleRows),
    [visibleRows],
  );

  const periodLabel = formatInvoiceDetailPeriodLabel(fromDate, toDate);
  const dirtyCount = rows.filter((r) => r.dirty).length;
  const totalTrades = visibleRows.length;

  const handleGenerate = async () => {
    if (!fromDate && !toDate) {
      toast.error("Select a from and/or to date for the period.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/trades");
      const trades = res.data || [];
      setAllTrades(trades);
      const built = buildInvoiceDetailRows(trades, fromDate, toDate, "");
      setRows(built);
      setShowReport(true);
      const matchCount = statusFilter
        ? built.filter((row) => row.status === statusFilter).length
        : built.length;
      if (!matchCount) {
        toast.info(
          statusFilter
            ? `No trades with status "${statusFilter}" in that period.`
            : "No trades with an entry date in that period.",
        );
      }
    } catch (err) {
      console.error(err);
      setError("Could not load trades for the invoice detail report.");
      toast.error("Could not load trades.");
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (rowId, key, value) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.rowId !== rowId) return row;
        const next = { ...row, [key]: value, dirty: true };
        if (key === "tradeNumber") {
          const match = allTrades.find(
            (t) =>
              String(t.tradeNumber || t.keyInfo?.tradeNumber) === String(value),
          );
          if (match) {
            const rebuilt =
              buildInvoiceDetailRows([match], fromDate, toDate, "")[0] ||
              buildInvoiceDetailRow(match);
            if (rebuilt) {
              return {
                ...rebuilt,
                rowId: row.rowId,
                dirty: true,
              };
            }
          }
        }
        return next;
      }),
    );
  };

  const handleSaveOverrides = async () => {
    const dirty = rows.filter((r) => r.dirty && r.tradeId);
    if (!dirty.length) {
      toast.info("No overrides to save.");
      return;
    }
    setSaving(true);
    try {
      for (const row of dirty) {
        const patch = invoiceDetailRowToTradePatch(row);
        await axiosInstance.put(`/trades/${row.tradeId}`, {
          keyInfo: patch.keyInfo,
        });
      }
      setRows((prev) => prev.map((r) => ({ ...r, dirty: false })));
      toast.success(`Saved overrides for ${dirty.length} trade(s).`);
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.message || "Failed to save trade overrides.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadCsv = () => {
    const headers = [
      ...INVOICE_DETAIL_COLUMNS.map((c) => c.label),
      "Total Trades in the Period",
    ];
    const data = visibleRows.map((row) => [
      ...INVOICE_DETAIL_COLUMNS.map((col) => row[col.key] ?? ""),
      "",
    ]);
    if (data.length) {
      data[data.length - 1][INVOICE_DETAIL_COLUMNS.length] = String(totalTrades);
    }
    const csv = buildCsv(headers, data);
    downloadCsvFile(
      `invoice-detail-report_${fromDate || "start"}_${toDate || "end"}.csv`,
      csv,
    );
  };

  const handlePrint = () => {
    const bodyRows = visibleRows
      .map((row) => {
        const cells = INVOICE_DETAIL_COLUMNS.map((col) => {
          const val = row[col.key] ?? "";
          return `<td>${escapeHtml(val)}</td>`;
        }).join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");

    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(REPORT_TITLE)}</title>
  <style>
    @page { size: portrait; margin: 12mm; }
    * { box-sizing: border-box; }
    html, body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 9px;
      color: #111;
      margin: 0;
      padding: 0;
      width: 100%;
    }
    .header {
      text-align: center;
      margin-bottom: 10px;
    }
    .header .logo {
      display: block;
      height: 48px;
      width: auto;
      margin: 0 auto 6px;
    }
    .company { font-size: 12px; font-weight: 700; }
    .title { font-size: 13px; font-weight: 700; margin-top: 2px; }
    .period { color: #444; margin-top: 2px; font-size: 9px; }
    .table-wrap {
      width: 100%;
      border: 1px solid #000;
      overflow: hidden;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      border: none;
    }
    th, td {
      border: 1px solid #000;
      padding: 3px 4px;
      text-align: left;
      vertical-align: top;
      word-wrap: break-word;
      overflow-wrap: anywhere;
      font-size: 9px;
      line-height: 1.25;
    }
    th {
      background: #f3f4f6;
      font-size: 8.5px;
      font-weight: 700;
    }
    .footer {
      margin-top: 10px;
      font-size: 10px;
      font-weight: 700;
    }
    @media print {
      html, body { margin: 0; padding: 0; }
      .table-wrap { border: 1px solid #000 !important; }
      th, td { border: 1px solid #000 !important; }
    }
  </style>
</head>
<body>
  <div class="header">
    <img class="logo" src="${logo}" alt="Logo" />
    <div class="company">${escapeHtml(companyName)}</div>
    <div class="title">${escapeHtml(REPORT_TITLE)}</div>
    <div class="period">${escapeHtml(periodLabel || "Selected period")}</div>
  </div>
  <div class="table-wrap">
    <table>
      <thead>
        <tr>
          ${INVOICE_DETAIL_COLUMNS.map((c) => `<th>${escapeHtml(c.label)}</th>`).join("")}
        </tr>
      </thead>
      <tbody>
        ${bodyRows || `<tr><td colspan="${INVOICE_DETAIL_COLUMNS.length}">No rows</td></tr>`}
      </tbody>
    </table>
  </div>
  <div class="footer">Total Trades in the Period: ${totalTrades}</div>
</body>
</html>`;
    const w = window.open("", "_blank", "width=1200,height=900");
    if (!w) {
      toast.error("Pop-up blocked. Allow pop-ups to print.");
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => {
      try {
        w.print();
      } catch (_) {}
    }, 300);
  };

  const renderCell = (row, col) => {
    const value = row[col.key] ?? "";

    if (col.edit === "text") {
      return (
        <input
          type="text"
          className={cellInputClass}
          value={value}
          onChange={(e) => updateRow(row.rowId, col.key, e.target.value)}
        />
      );
    }

    const options = getInvoiceDetailSelectOptions(
      col.key,
      row,
      tradeOptions,
      agentOptions,
      dateOptions,
    );
    const hasValue = options.some((o) => o.value === value);
    return (
      <select
        className={cellSelectClass}
        value={hasValue ? value : value || ""}
        onChange={(e) => updateRow(row.rowId, col.key, e.target.value)}
      >
        {!hasValue && value ? (
          <option value={value}>{value}</option>
        ) : (
          <option value="">—</option>
        )}
        {options.map((o) => (
          <option key={`${col.key}-${o.value}`} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Navbar />
      <div className="flex-1 p-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Invoice Detail Report
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Select a closing-date period (from / to), then override row
                values before printing or saving. Admin and Super User only.
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                navigate(
                  isFinancePage ? "/finance" : "/financial-reports",
                )
              }
              className="text-sm text-blue-700 hover:underline"
            >
              ← Back to Reports
            </button>
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-6">
            <h2 className="text-sm font-semibold text-gray-800 mb-3">
              Period selection
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  From (entry date)
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  To (entry date)
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Status filter
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  {INVOICE_DETAIL_STATUS_FILTER.map((o) => (
                    <option key={o.value || "any"} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-full sm:w-auto px-4 py-2 bg-blue-900 text-white rounded text-sm font-medium hover:bg-blue-800 disabled:opacity-60"
                >
                  {loading ? "Generating…" : "Generate report"}
                </button>
              </div>
            </div>
            {error ? (
              <p className="text-sm text-red-600 mt-3">{error}</p>
            ) : null}
          </div>

          {showReport ? (
            <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="text-center w-full sm:w-auto sm:text-left">
                  <img
                    src={logo}
                    alt="Company logo"
                    className="h-10 mx-auto sm:mx-0 mb-2"
                  />
                  <div className="text-sm font-semibold text-gray-500">
                    {companyName}
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {REPORT_TITLE}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {periodLabel || "Selected period"}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {visibleRows.length} row(s)
                    {statusFilter ? ` · filter: ${statusFilter}` : ""}
                    {dirtyCount
                      ? ` · ${dirtyCount} unsaved override(s)`
                      : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 justify-center sm:justify-end w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={handleSaveOverrides}
                    disabled={saving || !dirtyCount}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    <FaSave /> {saving ? "Saving…" : "Save overrides"}
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadCsv}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50"
                  >
                    <FaDownload /> CSV
                  </button>
                  <button
                    type="button"
                    onClick={handlePrint}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded bg-blue-900 text-white hover:bg-blue-800"
                  >
                    <FaPrint /> Print
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded">
                <table className="min-w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-100 text-left">
                      {INVOICE_DETAIL_COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          className="border border-gray-200 px-2 py-2 font-semibold whitespace-nowrap"
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={INVOICE_DETAIL_COLUMNS.length}
                          className="border px-3 py-6 text-center text-gray-500"
                        >
                          No trades in this period
                          {statusFilter
                            ? ` with status "${statusFilter}"`
                            : ""}
                          .
                        </td>
                      </tr>
                    ) : (
                      visibleRows.map((row) => (
                        <tr
                          key={row.rowId}
                          className={
                            row.dirty ? "bg-amber-50/60" : "bg-white"
                          }
                        >
                          {INVOICE_DETAIL_COLUMNS.map((col) => (
                            <td
                              key={`${row.rowId}-${col.key}`}
                              className="border border-gray-200 px-1 py-1 align-top"
                            >
                              {renderCell(row, col)}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <p className="text-sm font-semibold text-gray-800 mt-4">
                Total Trades in the Period: {totalTrades}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Dropdowns for date, trade #, agent, type, and status. Property
                address is free text. Dates use MM/DD/YYYY.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailReport;
